import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { Autocomplete } from '@guildeducationinc/recess';
import { useIntl } from 'react-intl';
import { useAllTimezones } from '../..';
/**
 * A component that renders a timezone select dropdown.
 *
 * @component
 * @param {TimezoneSelectProps} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.label - The label for the select dropdown.
 * @param {string} props.value - The selected value of the select dropdown.
 * @param {string[]} props.errors - The errors associated with the select dropdown.
 * @param {Function} props.onChange - The callback function to handle the change event of the select dropdown.
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * ```tsx
 * <TimezoneSelect
 *   className="timezone-select"
 *   label="Select Timezone"
 *   value={selectedTimezone}
 *   errors={timezoneErrors}
 *   onChange={handleTimezoneChange}
 * />
 * ```
 */
export const TimezoneSelect = ({ className, label, value, errors, onChange, }) => {
    const { options } = useAllTimezones();
    const intl = useIntl();
    if (!label)
        label = intl.formatMessage({ id: 'Timezone' });
    const [selectedTimezone, setSelectedTimezone] = useState(null);
    const handleChange = (e) => {
        const value = (e.target.value || '').toString();
        onChange(value);
        setSelectedTimezone(value);
    };
    /**
     * Handles the loading of options for the select dropdown.
     *
     * @param {string} query - The search query.
     * @param {Function} callback - The callback function to receive the matching options.
     * @returns {void}
     */
    const handleLoad = (query, callback) => {
        if (!options)
            return;
        const matches = options.filter(opt => {
            if (!opt.label)
                return false;
            return opt.label.toLowerCase().includes((query || '').toLowerCase());
        });
        callback(matches);
    };
    return (_jsx(Autocomplete, { className: className, label: label, value: selectedTimezone || value, loadOptions: handleLoad, onChange: handleChange, cacheOptions: true, errors: errors }));
};
